import React, { useState } from 'react';
import { sendEmail } from '../utils/emailService';
import { showSuccessAlert } from '../utils/alertService';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [focusedField, setFocusedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMessage(null);

    const serviceLabels = {
      bpo: 'BPO Services',
      it: 'IT Services',
      webdev: 'Website Development',
      webdesign: 'Website Design',
      marketing: 'Digital Marketing',
      logo: 'Logo Design'
    };

    const templateParams = {
      from_name: formData.name,
      user_name: formData.name,
      name: formData.name,
      from_email: formData.email,
      user_email: formData.email,
      email: formData.email,
      phone: formData.phone,
      user_phone: formData.phone,
      service: serviceLabels[formData.service] || formData.service,
      service_interested: serviceLabels[formData.service] || formData.service,
      message: formData.message,
      time: new Date().toLocaleString(),
      form_type: 'General Contact / Quote Inquiry'
    };

    const res = await sendEmail(templateParams);
    setLoading(false);

    showSuccessAlert(
      'Message Sent!',
      `Thank you, ${formData.name || 'valued client'}! Your message has been sent successfully. Our team will contact you shortly.`
    );

    setStatusMessage({
      type: 'success',
      text: `Thank you, ${formData.name || 'valued client'}! Your message has been sent successfully.`
    });
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  return (
    <section className="contact-section" id="contact-form">
      <div className="container">
        
        {/* Contact Header */}
        <div className="contact-header">
          <div className="contact-label-row">
            {/* Red Circle/Hexagon Icon */}
            <div className="contact-label-icon">
              <span className="dot-left"></span>
              <span className="dot-right"></span>
            </div>
            <span className="contact-label-text">Contact Us</span>
          </div>
          <h2 className="contact-title">Get Your Free Quote Today!</h2>
        </div>

        {/* Contact Content Grid */}
        <div className="contact-grid">
          
          {/* Left Column: Form */}
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-form-row">
              <div className={`wrap-contact-input ${focusedField === 'name' ? 'input-focused' : ''} ${formData.name ? 'has-val' : ''}`}>
                <label className="label-contact-input" htmlFor="name">
                  Your Name *
                </label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  className="contact-input-field"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => handleFocus('name')}
                  onBlur={handleBlur}
                  maxLength="70"
                  required 
                />
              </div>
              
              <div className={`wrap-contact-input ${focusedField === 'email' ? 'input-focused' : ''} ${formData.email ? 'has-val' : ''}`}>
                <label className="label-contact-input" htmlFor="email">
                  Email *
                </label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  className="contact-input-field"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => handleFocus('email')}
                  onBlur={handleBlur}
                  required 
                />
              </div>
            </div>

            <div className="contact-form-row">
              <div className={`wrap-contact-input ${focusedField === 'phone' ? 'input-focused' : ''} ${formData.phone ? 'has-val' : ''}`}>
                <label className="label-contact-input" htmlFor="phone">
                  Phone *
                </label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  className="contact-input-field"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => handleFocus('phone')}
                  onBlur={handleBlur}
                  required 
                />
              </div>

              <div className={`wrap-contact-input select-field-wrapper ${focusedField === 'service' ? 'input-focused' : ''} ${formData.service ? 'has-val' : ''}`}>
                <label className="label-contact-input" htmlFor="service">
                  Service Interested In *
                </label>
                <select 
                  id="service" 
                  name="service" 
                  className="contact-input-field"
                  value={formData.service}
                  onChange={handleChange}
                  onFocus={() => handleFocus('service')}
                  onBlur={handleBlur}
                  required
                >
                  <option value="" disabled></option>
                  <option value="bpo">BPO Services</option>
                  <option value="it">IT Services</option>
                  <option value="webdev">Website Development</option>
                  <option value="webdesign">Website Design</option>
                  <option value="marketing">Digital Marketing</option>
                  <option value="logo">Logo Design</option>
                </select>
              </div>
            </div>

            <div className={`wrap-contact-input textarea-field-wrapper ${focusedField === 'message' ? 'input-focused' : ''} ${formData.message ? 'has-val' : ''}`}>
              <label className="label-contact-input" htmlFor="message">
                Your Message *
              </label>
              <textarea 
                id="message" 
                name="message" 
                className="contact-input-field contact-textarea"
                rows="5" 
                value={formData.message}
                onChange={handleChange}
                onFocus={() => handleFocus('message')}
                onBlur={handleBlur}
                required
              ></textarea>
            </div>

            {statusMessage && (
              <div 
                style={{
                  padding: '14px 18px',
                  borderRadius: '12px',
                  marginBottom: '1.2rem',
                  backgroundColor: 'rgba(34, 197, 94, 0.12)',
                  border: '1px solid #22c55e',
                  color: '#15803d',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{statusMessage.text}</span>
              </div>
            )}

            <button type="submit" className="btn-contact-submit" disabled={loading}>
              {loading ? 'Sending Message...' : 'Send Message'}
            </button>
          </form>

          {/* Right Column: Image with Sparkles */}
          <div className="contact-image-container">
            <div className="contact-img-wrapper">
              <img 
                src="/assets/images/contact_team_bw.png" 
                alt="Our Team" 
                className="contact-team-img"
              />
              
              {/* Floating Sparkle Icons (Red instead of Green) */}
              <div className="sparkle sparkle-large">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L14.6 9.4L24 12L14.6 14.6L12 24L9.4 14.6L0 12L9.4 9.4L12 0Z" />
                </svg>
              </div>
              <div className="sparkle sparkle-medium">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L14.6 9.4L24 12L14.6 14.6L12 24L9.4 14.6L0 12L9.4 9.4L12 0Z" />
                </svg>
              </div>
              <div className="sparkle sparkle-small">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L14.6 9.4L24 12L14.6 14.6L12 24L9.4 14.6L0 12L9.4 9.4L12 0Z" />
                </svg>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default ContactUs;
